/**
 * tooltipEdgeGuard — keeps every tooltip bubble inside the browser window.
 *
 * Why: the design-system bubble (`.amd-tip__bubble`, used by the library
 * Tooltip and by our FloatingTooltip) is always centred on its target, with no
 * idea where the window ends. Near an edge — the sidebar's My/Org switch, a
 * button at the right of the app bar — half of it hangs out of the window.
 * CSS alone cannot measure the window, so this watches for bubbles appearing
 * and, for each one:
 *   1. flips it to the opposite side if its own side runs out of room
 *      (`data-amd-tip-flip`), and
 *   2. slides it along that side until it fits (`--amd-tip-shift-x/-y`),
 *      moving the arrow back the same amount so it still points at the target
 *      (`--amd-tip-arrow-x/-y`), and
 *   3. caps its width at the visible window (`--amd-tip-max-w`), so a label
 *      longer than that wraps.
 * global.css turns those into the actual position. Nothing per-tooltip:
 * every tooltip in the app gets it, present and future.
 *
 * `installTooltipEdgeGuard()` is called once from main.jsx; the module itself
 * has no side effects.
 */

/** Room, in px, kept clear between a bubble and the window edge. */
const EDGE = 8;
const BUBBLE = '.amd-tip__bubble';
const OPPOSITE = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
const SIDES = Object.keys(OPPOSITE);
const VARS = ['--amd-tip-shift-x', '--amd-tip-shift-y', '--amd-tip-arrow-x', '--amd-tip-arrow-y'];

function sideOf(bubble) {
  return SIDES.find((s) => bubble.classList.contains(`amd-tip__bubble--${s}`)) ?? 'top';
}

/** How far the box sticks out past the window on `side` (0 if it doesn't). */
function overflow(r, side, vw, vh) {
  if (side === 'top') return Math.max(0, EDGE - r.top);
  if (side === 'bottom') return Math.max(0, r.bottom - (vh - EDGE));
  if (side === 'left') return Math.max(0, EDGE - r.left);
  return Math.max(0, r.right - (vw - EDGE));
}

/**
 * The shift that brings [start, end] inside [EDGE, size - EDGE]. A box longer
 * than the window keeps its START edge visible (wrapping in global.css makes
 * that rare).
 */
function shiftInto(start, end, size) {
  if (start < EDGE) return EDGE - start;
  if (end > size - EDGE) return Math.max(EDGE - start, size - EDGE - end);
  return 0;
}

/** The arrow's counter-shift, kept on the bubble (clear of its rounded corners). */
function arrowShift(shift, length) {
  const room = Math.max(0, length / 2 - 10);
  return Math.max(-room, Math.min(room, -shift));
}

/** Fit one bubble into the window. Starts from the library's placement each time. */
export function fitBubble(bubble, win = window) {
  const vw = win.document.documentElement.clientWidth;
  const vh = win.document.documentElement.clientHeight;
  if (!vw || !vh) return; // no layout to measure (tests, a hidden frame)

  delete bubble.dataset.amdTipFlip;
  VARS.forEach((v) => bubble.style.removeProperty(v));
  // The widest a bubble may be: the VISIBLE width (100vw would include the
  // page scrollbar), so a long label wraps inside the window.
  bubble.style.setProperty('--amd-tip-max-w', `${vw - 2 * EDGE}px`);

  const side = sideOf(bubble);
  let r = bubble.getBoundingClientRect();
  const out = overflow(r, side, vw, vh);
  if (out > 0) {
    // Flip only if the other side is actually better.
    bubble.dataset.amdTipFlip = OPPOSITE[side];
    const flipped = bubble.getBoundingClientRect();
    if (overflow(flipped, OPPOSITE[side], vw, vh) < out) r = flipped;
    else delete bubble.dataset.amdTipFlip;
  }

  // Slide it inside on both axes. Across its side this keeps it beside the
  // target (the arrow moves back to keep pointing at it); along its side it
  // only happens when NEITHER side had room — then it overlaps the target
  // rather than leave the window.
  const vertical = side === 'top' || side === 'bottom';
  const dx = shiftInto(r.left, r.right, vw);
  const dy = shiftInto(r.top, r.bottom, vh);
  if (dx) bubble.style.setProperty('--amd-tip-shift-x', `${dx}px`);
  if (dy) bubble.style.setProperty('--amd-tip-shift-y', `${dy}px`);
  if (vertical && dx) bubble.style.setProperty('--amd-tip-arrow-x', `${arrowShift(dx, r.width)}px`);
  if (!vertical && dy)
    bubble.style.setProperty('--amd-tip-arrow-y', `${arrowShift(dy, r.height)}px`);
}

/**
 * Watch the page: fit each bubble as it appears or its text changes, on
 * window resize and on any scroll. Returns a cleanup function. (The entry
 * animation in global.css is a fade only, never a scale, so the first
 * measurement is already the bubble's real size.)
 */
export function installTooltipEdgeGuard(win = window) {
  const doc = win?.document;
  if (!doc?.body || typeof win.MutationObserver === 'undefined') return () => {};

  const fit = (bubble) => fitBubble(bubble, win);
  const fitAll = () => doc.querySelectorAll(BUBBLE).forEach(fit);

  const observer = new win.MutationObserver((records) => {
    const touched = new Set();
    records.forEach((rec) => {
      const el = rec.target.nodeType === 1 ? rec.target : rec.target.parentElement;
      const inside = el?.closest?.(BUBBLE);
      if (inside) touched.add(inside);
      rec.addedNodes.forEach((n) => {
        if (n.nodeType !== 1) return;
        if (n.matches(BUBBLE)) touched.add(n);
        n.querySelectorAll(BUBBLE).forEach((b) => touched.add(b));
      });
    });
    touched.forEach(fit);
  });
  observer.observe(doc.body, { childList: true, subtree: true, characterData: true });

  let frame = 0;
  const schedule = () => {
    if (frame) return;
    frame = win.requestAnimationFrame(() => {
      frame = 0;
      fitAll();
    });
  };
  win.addEventListener('resize', schedule);
  win.addEventListener('scroll', schedule, true); // capture: any scrolling box

  return () => {
    observer.disconnect();
    win.removeEventListener('resize', schedule);
    win.removeEventListener('scroll', schedule, true);
    if (frame) win.cancelAnimationFrame(frame);
  };
}
